import { clamp, mean, median, std } from './math';
import { unwrappedCents } from './note';

import type { PitchSample, Vibrato } from './types';

/**
 * vibrato — pitch oscillation of a held note, measured as rate / depth / regularity.
 *
 * This is a *time-series* analysis, which is why it lives here and not in the native
 * pitch core: a single frame cannot show a cycle, and giving the C tracker memory of
 * past frames would break its re-entrancy contract. It is also why the input is
 * `PitchSample[]` and not `Clip` — `Clip.pitches` has no time base, so "5.5 per
 * second" is unanswerable from it.
 *
 * **Silences are boundaries, not data.** A breath in the middle of a take is not a
 * long slow swing; stitching across one halves the reported rate. So the analysis
 * runs inside a single unbroken voiced run and reports on the longest one.
 *
 * Framework-free and pure — vitest target.
 */

/**
 * Longest hole that still counts as the same run (ms).
 *
 * The fastest vibrato we accept is 10 Hz, one cycle per 100 ms. A hole of 60 ms is
 * over half of that: whatever the pitch did across it, we did not see the peak, and
 * joining the two sides invents one. Deliberately far tighter than the half-second a
 * caller may allow when measuring phonation time — that question is "did the voice
 * stop", this one is "did we watch a whole cycle".
 */
const MAX_GAP_MS = 60;

/**
 * Rate band we are willing to call vibrato (Hz). Singing vibrato sits around 4-7 Hz
 * (the screen quotes 5-6.5 Hz as the reference band); below 3 Hz a held note is
 * wavering and above 10 Hz it is a tremor. Outside the band we report nothing rather
 * than putting a rate on a phenomenon this axis does not describe.
 */
const MIN_RATE_HZ = 3;
const MAX_RATE_HZ = 10;

/**
 * A cycle has to swing at least this far each way (cents) to be counted. 10 cents is
 * a tenth of a semitone — under it, what crosses the centre line is tracker noise on
 * a steady note, and counting it would give a dead-flat take a rate and a depth.
 */
const MIN_EXCURSION_CENTS = 10;

/**
 * Cycles needed before we report. Regularity is the spread of the cycle lengths, and
 * a spread of two numbers is not one — three is the smallest count where "even" and
 * "uneven" are different observations.
 */
const MIN_CYCLES = 3;

/**
 * Detrend window for the first pass (ms), used only to find the cycles. It has to be
 * longer than the slowest period we accept (1/3 Hz = 333 ms) or the moving average
 * would follow the vibrato instead of the drift underneath it.
 */
const PROBE_WINDOW_MS = 500;

/**
 * Splits samples into runs of continuous phonation.
 *
 * The gap is a parameter because the two questions asked of it are different sizes:
 * "did we watch a whole vibrato cycle" tolerates 60 ms, "did the voice stop" tolerates
 * half a second. Only the threshold differs — the splitting itself is one algorithm,
 * so it is written once here rather than re-derived per caller.
 */
export const voicedRuns = (
  samples: PitchSample[],
  maxGapMs: number = MAX_GAP_MS,
): PitchSample[][] => {
  const runs: PitchSample[][] = [];
  let current: PitchSample[] = [];
  for (const sample of samples) {
    const previous = current[current.length - 1];
    if (previous && sample.tMs - previous.tMs > maxGapMs) {
      runs.push(current);
      current = [];
    }
    current.push(sample);
  }
  if (current.length > 0) {
    runs.push(current);
  }
  return runs;
};

/** Wall time a run spans (s). A single reading spans nothing. */
export const runSeconds = (run: PitchSample[]): number =>
  (run[run.length - 1].tMs - run[0].tMs) / 1000;

/**
 * Pitch as cents around the run's own median — the note being held is the zero —
 * with octave jumps unwrapped (`unwrappedCents`). Without it one octave-error frame
 * inside a cycle turns a ±50 cents swing into a ±600 one, and cycles take their swing
 * from the extremes: 3 % of frames an octave low moved a ±50 cents fixture to ±215
 * (TestFlight #70 reported ±271). The frame and its time stamp stay — dropping it
 * would leave a hole the detrend and the zero crossings interpolate across.
 */
const toCents = (run: PitchSample[]): number[] => {
  const f0 = run.map((s) => s.f0Hz);
  return unwrappedCents(f0, median(f0));
};

/**
 * Subtracts a centred moving average, leaving the oscillation. A held note sags and
 * recovers by more than the wobble itself, and an undetrended sag never crosses its
 * own centre line — no crossings, no cycles, no measurement.
 *
 * The average is the **area** under the piecewise-linear pitch curve over the window,
 * not the mean of the samples inside it. Averaging samples is what a first draft does
 * and it eats the signal: at ~8 frames per cycle a window of one period holds 7 or 9
 * of them, never a whole turn, so a slice of the oscillation survives into the
 * "trend" and comes back out of the depth (measured: 12 % short on a 6 Hz fixture).
 * Integrating to the exact window edge is zero for a wave of that period, whatever
 * the frame rate.
 */
const detrend = (
  cents: number[],
  times: number[],
  windowMs: number,
): number[] => {
  const half = windowMs / 2;
  const first = times[0];
  const last = times[times.length - 1];
  const at = (t: number, j: number): number => {
    const span = times[j] - times[j - 1];
    return span === 0
      ? cents[j]
      : cents[j - 1] + ((t - times[j - 1]) / span) * (cents[j] - cents[j - 1]);
  };

  let lo = 1;
  let hi = 1;
  return cents.map((value, i) => {
    const from = Math.max(first, times[i] - half);
    const to = Math.min(last, times[i] + half);
    while (lo < times.length - 1 && times[lo] <= from) lo++;
    while (hi < times.length - 1 && times[hi] < to) hi++;
    let area = 0;
    for (let j = lo; j <= hi; j++) {
      const a = Math.max(from, times[j - 1]);
      const b = Math.min(to, times[j]);
      if (b > a) {
        area += ((at(a, j) + at(b, j)) / 2) * (b - a);
      }
    }
    return to > from ? value - area / (to - from) : 0;
  });
};

interface Cycle {
  /** Seconds from one upward zero crossing to the next. */
  periodSec: number;
  /** Peak-to-peak swing within the cycle, in cents. */
  swingCents: number;
}

/** Upward zero crossings, linearly interpolated so the period beats frame resolution. */
const upwardCrossings = (signal: number[], times: number[]): number[] => {
  const crossings: number[] = [];
  for (let i = 1; i < signal.length; i++) {
    if (signal[i - 1] < 0 && signal[i] >= 0) {
      const span = signal[i] - signal[i - 1];
      const frac = span === 0 ? 0 : -signal[i - 1] / span;
      crossings.push(times[i - 1] + frac * (times[i] - times[i - 1]));
    }
  }
  return crossings;
};

/**
 * Cycles between consecutive crossings, keeping only the ones that both swing far
 * enough and last a plausible time. A rejected cycle is dropped, not clamped: a
 * 400 ms "cycle" spanning a dropout is not a slow vibrato.
 */
const cyclesOf = (signal: number[], times: number[], edgeMs = 0): Cycle[] => {
  const crossings = upwardCrossings(signal, times);
  const inner = times[0] + edgeMs;
  const outer = times[times.length - 1] - edgeMs;
  const cycles: Cycle[] = [];
  // `times` and the crossings are both ascending, so the window only ever moves
  // forward: carry the start index across cycles and walk the few samples inside
  // one, instead of rescanning the whole run per cycle.
  let head = 0;
  for (let c = 1; c < crossings.length; c++) {
    const from = crossings[c - 1];
    const to = crossings[c];
    if (from < inner || to > outer) {
      continue;
    }
    const periodSec = (to - from) / 1000;
    if (periodSec < 1 / MAX_RATE_HZ || periodSec > 1 / MIN_RATE_HZ) {
      continue;
    }
    while (head < times.length && times[head] < from) {
      head++;
    }
    let high = -Infinity;
    let low = Infinity;
    for (let i = head; i < times.length && times[i] <= to; i++) {
      high = Math.max(high, signal[i]);
      low = Math.min(low, signal[i]);
    }
    if (high === -Infinity) {
      continue;
    }
    if (high < MIN_EXCURSION_CENTS || low > -MIN_EXCURSION_CENTS) {
      continue;
    }
    cycles.push({ periodSec, swingCents: high - low });
  }
  return cycles;
};

/**
 * Measures the longest unbroken voiced run. `null` when there is nothing to measure —
 * too short, too flat, or oscillating outside the vibrato band.
 */
export const analyzeVibrato = (samples: PitchSample[]): Vibrato | null => {
  const runs = voicedRuns(samples).filter((run) => run.length >= 2);
  if (runs.length === 0) {
    return null;
  }
  const run = runs.reduce((longest, candidate) =>
    runSeconds(candidate) > runSeconds(longest) ? candidate : longest,
  );

  const times = run.map((s) => s.tMs);
  const cents = toCents(run);
  const probe = cyclesOf(detrend(cents, times, PROBE_WINDOW_MS), times);
  if (probe.length < MIN_CYCLES) {
    return null;
  }

  /**
   * Second pass. The probe window is a couple of vibrato periods wide, so it leaves
   * a few percent of the oscillation in the average it subtracts and the depth comes
   * out short. Re-running with a window of exactly one period fixes that: a sine
   * averages to zero over its own period, so the trend goes and the swing stays.
   * The crossings barely move, so the rate from either pass is the same.
   */
  const periodMs = 1000 * mean(probe.map((c) => c.periodSec));
  /**
   * Half a period is dropped from each end. The moving average has no samples past
   * the edge of the run, so the trend there is estimated from one side only and the
   * crossings under it sit a little off — on a 1.5 s fixture that bias alone moved
   * the rate 2 %. Cycles in the middle are under a symmetric window and are clean.
   */
  const cycles = cyclesOf(detrend(cents, times, periodMs), times, periodMs / 2);
  if (cycles.length < MIN_CYCLES) {
    return null;
  }

  const periods = cycles.map((c) => c.periodSec);
  const meanPeriod = mean(periods);
  return {
    rateHz: 1 / meanPeriod,
    depthCents: mean(cycles.map((c) => c.swingCents)) / 2,
    regularityPct: Math.round(clamp(100 * (1 - std(periods) / meanPeriod))),
  };
};

/**
 * Reference band for the rate (Hz) — the design's `참고 구간 5-6.5Hz` (§8.6).
 *
 * The screen quotes it and the vibrato axis is scored against it: one constant, so
 * the caption and the score cannot describe two different bands.
 */
export const VIBRATO_REFERENCE_HZ = { min: 5, max: 6.5 };

/** Reference band for the depth (cents) — the design's `참고 구간 ±50-120 cents` (§8.6). */
export const VIBRATO_DEPTH_REFERENCE_CENTS = { min: 50, max: 120 };

/**
 * How far outside a band a value may go before it scores zero. One band-floor
 * below the band for depth (a ±25 cents swing is half of a vibrato), 3 Hz for rate
 * (8 Hz reads as a flutter, 3.5 Hz as a wobble). Uncalibrated — no reviewed takes yet.
 */
const DEPTH_FALLOFF_CENTS = VIBRATO_DEPTH_REFERENCE_CENTS.min;
const RATE_FALLOFF_HZ = 3;

const bandScore = (
  value: number,
  band: { min: number; max: number },
  falloff: number,
): number => {
  const distance =
    value < band.min ? band.min - value : Math.max(0, value - band.max);
  return clamp(100 * (1 - distance / falloff));
};

/**
 * Rate and depth each scored 0-100 against their reference band. Exported so the
 * screen can name the weaker of the two with the same rule the score uses.
 */
export const vibratoBandScores = (
  vibrato: Vibrato,
): { rate: number; depth: number } => ({
  rate: bandScore(vibrato.rateHz, VIBRATO_REFERENCE_HZ, RATE_FALLOFF_HZ),
  depth: bandScore(
    vibrato.depthCents,
    VIBRATO_DEPTH_REFERENCE_CENTS,
    DEPTH_FALLOFF_CENTS,
  ),
});

/**
 * The vibrato axis (0-100) — how close the measured rate and depth sit to the
 * reference bands the result screen draws. The worse of the two is the score: a
 * textbook rate does not make a ±271 cents swing a vibrato.
 *
 * Takes the measured `Vibrato`, never the pitch array: the axis used to be scored from
 * cents already folded to ±50 around the nearest semitone, so its amplitude test was
 * true for nearly any take and a ±271 cents swing scored 100.
 */
export const vibratoAxisScore = (vibrato: Vibrato): number => {
  const { rate, depth } = vibratoBandScores(vibrato);
  return Math.min(rate, depth);
};
